import {
  filterPermittedActionsWithDividers,
  parseActionsOnValidDivider,
} from '../../utils/massActions'
import { isSubArchive } from '../../utils/route'
import {
  isStaffUser,
  isUserStatusCodeActive,
  isUserStatusCodeBlocked,
  isUserStatusCodeNotConfirmed,
} from '../../utils/user'

import {
  INHABITANT_ACTION_APPROVE_REGISTRATION_ITEM,
  INHABITANT_ACTION_APPROVE_REGISTRATION_MASS,
  INHABITANT_ACTION_ARCHIVE_ITEM,
  INHABITANT_ACTION_ARCHIVE_MASS,
  INHABITANT_ACTION_BLOCK_ITEM,
  INHABITANT_ACTION_BLOCK_MASS,
  INHABITANT_ACTION_CHANGE_ADDRESS,
  INHABITANT_ACTION_EDIT_ROLE_ITEM,
  INHABITANT_ACTION_EDIT_ROLE_MASS,
  INHABITANT_ACTION_MASS_EMAIL,
  INHABITANT_ACTION_RESTORE_ITEM,
  INHABITANT_ACTION_RESTORE_MASS,
  INHABITANT_ACTION_SEND_INVITE_ITEM,
  INHABITANT_ACTION_SEND_INVITE_MASS,
  INHABITANT_ACTION_UNBLOCK_ITEM,
  INHABITANT_ACTION_UNBLOCK_MASS,
} from './Inhabitant.constants'

export function isInhabitantActionEditRoleMass(action) {
  return action === INHABITANT_ACTION_EDIT_ROLE_MASS
}

export function getCanViewArchivedInhabitants({
  currentUser,
  isArchivedUsersDisplayed,
}) {
  return Boolean(isArchivedUsersDisplayed && isStaffUser(currentUser))
}

export function getInhabitantTableNotificationTitle({
  action,
  count,
  t,
  totalCount,
}) {
  const types = {
    [INHABITANT_ACTION_APPROVE_REGISTRATION_ITEM]: t(
      'Notification.InhabitantTitleApprovedKey'
    ),
    [INHABITANT_ACTION_APPROVE_REGISTRATION_MASS]: t(
      'Notification.InhabitantTitleMassApprovedKey',
      { count, totalCount }
    ),
    [INHABITANT_ACTION_ARCHIVE_ITEM]: t('Notification.CommonTitleArchivedKey'),
    [INHABITANT_ACTION_ARCHIVE_MASS]: t(
      'Notification.CommonTitleMassArchivedKey',
      { count, totalCount }
    ),
    [INHABITANT_ACTION_BLOCK_ITEM]: t('Notification.CommonTitleBlockedKey'),
    [INHABITANT_ACTION_BLOCK_MASS]: t(
      'Notification.CommonTitleMassBlockedKey',
      {
        count,
        totalCount,
      }
    ),
    [INHABITANT_ACTION_CHANGE_ADDRESS]: t('Notification.CommonTitleSavedKey'),
    [INHABITANT_ACTION_EDIT_ROLE_ITEM]: t('Notification.CommonTitleChangedKey'),
    [INHABITANT_ACTION_EDIT_ROLE_MASS]: t(
      'Notification.CommonTitleMassChangedKey',
      { count, totalCount }
    ),
    [INHABITANT_ACTION_RESTORE_ITEM]: t('Notification.CommonTitleRestoredKey'),
    [INHABITANT_ACTION_RESTORE_MASS]: t(
      'Notification.CommonTitleMassRestoredKey',
      { count, totalCount }
    ),
    [INHABITANT_ACTION_SEND_INVITE_ITEM]: t(
      'Notification.InhabitantTitleSentInviteKey'
    ),
    [INHABITANT_ACTION_SEND_INVITE_MASS]: t(
      'Notification.InhabitantTitleMassSentInviteKey',
      { count, totalCount }
    ),
    [INHABITANT_ACTION_UNBLOCK_ITEM]: t('Notification.CommonTitleUnblockedKey'),
    [INHABITANT_ACTION_UNBLOCK_MASS]: t(
      'Notification.CommonTitleMassUnblockedKey',
      { count, totalCount }
    ),
  }

  return types[action] || null
}

export function getInhabitantTableAsyncNotificationText({ action, t }) {
  const types = {
    [INHABITANT_ACTION_APPROVE_REGISTRATION_MASS]: t(
      'Notification.InhabitantAsyncTextMassApproveKey'
    ),
    [INHABITANT_ACTION_ARCHIVE_MASS]: t(
      'Notification.InhabitantAsyncTextMassArchiveKey'
    ),
    [INHABITANT_ACTION_BLOCK_MASS]: t(
      'Notification.InhabitantAsyncTextMassBlockKey'
    ),
    [INHABITANT_ACTION_EDIT_ROLE_MASS]: t(
      'Notification.InhabitantAsyncTextMassChangeRoleKey'
    ),
    [INHABITANT_ACTION_RESTORE_MASS]: t(
      'Notification.InhabitantAsyncTextMassRestoreKey'
    ),
    [INHABITANT_ACTION_SEND_INVITE_MASS]: t(
      'Notification.InhabitantAsyncTextMassSendInviteKey'
    ),
    [INHABITANT_ACTION_UNBLOCK_MASS]: t(
      'Notification.InhabitantAsyncTextMassUnblockKey'
    ),
  }

  return types[action] || null
}

export function getInhabitantTableItemActions({
  archived,
  permissions,
  status,
}) {
  const isBlocked = isUserStatusCodeBlocked(status)
  const isActive = isUserStatusCodeActive(status)
  const isNotConfirmed = isUserStatusCodeNotConfirmed(status)

  const items = [
    {
      label: 'Inhabitant.SendInviteActionOptionKey',
      permission: permissions.can_send_invite && !isActive,
      value: INHABITANT_ACTION_SEND_INVITE_ITEM,
    },
    {
      label: 'Inhabitant.ChangeAddressActionOptionKey',
      permission: permissions.can_manage_flats && !isNotConfirmed,
      value: INHABITANT_ACTION_CHANGE_ADDRESS,
    },
    {
      label: 'Inhabitant.ChangeRoleActionOptionKey',
      permission: permissions.can_update_role && !archived,
      value: INHABITANT_ACTION_EDIT_ROLE_ITEM,
    },
    {
      label: 'Inhabitant.ApproveRegistrationActionOptionKey',
      permission: permissions.can_approve_registration && isNotConfirmed,
      value: INHABITANT_ACTION_APPROVE_REGISTRATION_ITEM,
    },
    null,
    {
      label: 'Inhabitant.BlockActionOptionKey',
      permission: permissions.can_block && !isBlocked,
      value: INHABITANT_ACTION_BLOCK_ITEM,
    },
    {
      label: 'Inhabitant.UnblockActionOptionKey',
      permission: permissions.can_unblock && isBlocked,
      value: INHABITANT_ACTION_UNBLOCK_ITEM,
    },
    {
      label: 'Inhabitant.ArchiveActionOptionKey',
      permission: permissions.can_archive,
      value: INHABITANT_ACTION_ARCHIVE_ITEM,
    },
    {
      label: 'Inhabitant.RestoreActionOptionKey',
      permission: permissions.can_restore,
      value: INHABITANT_ACTION_RESTORE_ITEM,
    },
  ]

  return parseActionsOnValidDivider(filterPermittedActionsWithDividers(items))
}

export function getInhabitantTableMassActions({ permissions, sub, t }) {
  const isArchive = isSubArchive(sub)

  const items = [
    {
      label: t('Inhabitant.SendInviteActionOptionKey'),
      permission: permissions.can_send_invite && !isArchive,
      value: INHABITANT_ACTION_SEND_INVITE_MASS,
    },
    {
      label: t('Inhabitant.MassEmailActionOptionKey'),
      permission: permissions.can_create_mass_mailing && !isArchive,
      value: INHABITANT_ACTION_MASS_EMAIL,
    },
    {
      label: t('Inhabitant.ChangeRoleActionOptionKey'),
      permission: permissions.can_update_role && !isArchive,
      value: INHABITANT_ACTION_EDIT_ROLE_MASS,
    },
    {
      label: t('Inhabitant.ApproveRegistrationActionOptionKey'),
      permission: permissions.can_approve_registration && !isArchive,
      value: INHABITANT_ACTION_APPROVE_REGISTRATION_MASS,
    },
    null,
    {
      label: t('Inhabitant.BlockActionOptionKey'),
      permission: permissions.can_block && !isArchive,
      value: INHABITANT_ACTION_BLOCK_MASS,
    },
    {
      label: t('Inhabitant.UnblockActionOptionKey'),
      permission: permissions.can_unblock && !isArchive,
      value: INHABITANT_ACTION_UNBLOCK_MASS,
    },
    {
      label: t('Inhabitant.ArchiveActionOptionKey'),
      permission: permissions.can_archive && !isArchive,
      value: INHABITANT_ACTION_ARCHIVE_MASS,
    },
    {
      label: t('Inhabitant.RestoreActionOptionKey'),
      permission: permissions.can_restore && isArchive,
      value: INHABITANT_ACTION_RESTORE_MASS,
    },
  ]

  return parseActionsOnValidDivider(filterPermittedActionsWithDividers(items))
}

export function getInhabitantTableMassActionStatusApiParam(
  action,
  filterStatus
) {
  if (action === INHABITANT_ACTION_UNBLOCK_MASS) {
    return [3]
  }

  if (action === INHABITANT_ACTION_APPROVE_REGISTRATION_MASS) {
    return [4]
  }

  return filterStatus
}
